// src/services/analyticsService.js
import { api } from './api';
import { createService } from './factory';

// Create the base analytics service using the factory
const analyticsServiceBase = createService('Analytics', '/analytics', {
  customEndpoints: {
    /**
     * Get dashboard analytics
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} Dashboard analytics data
     */
    getDashboardAnalytics: async (params = {}) => {
      const response = await api.get('/analytics/dashboard', { params });
      return response;
    },
    
    /**
     * Get user activity analytics
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} User activity analytics data
     */
    getUserActivityAnalytics: async (params = {}) => {
      const response = await api.get('/analytics/user-activity', { params });
      return response;
    },
    
    /**
     * Get content analytics
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} Content analytics data
     */
    getContentAnalytics: async (params = {}) => {
      const response = await api.get('/analytics/content', { params });
      return response;
    },
    
    /**
     * Get campaign analytics
     * @param {string} campaignId - Campaign ID (optional)
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} Campaign analytics data
     */
    getCampaignAnalytics: async (campaignId, params = {}) => {
      const url = campaignId ? `/campaigns/${campaignId}/performance` : '/analytics/campaigns';
      const response = await api.get(url, { params });
      return response;
    },
    
    /**
     * Get client analytics
     * @param {string} clientId - Client ID
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} Client analytics data
     */
    getClientAnalytics: async (clientId, params = {}) => {
      const response = await api.get(`/analytics/clients/${clientId}`, { params });
      return response;
    },
    
    /**
     * Get brand analytics
     * @param {string} brandId - Brand ID
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} Brand analytics data
     */
    getBrandAnalytics: async (brandId, params = {}) => {
      const response = await api.get(`/analytics/brands/${brandId}`, { params });
      return response;
    },
    
    /**
     * Get social media performance analytics
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} Social media performance data
     */
    getSocialMediaPerformance: async (params = {}) => {
      const response = await api.get('/analytics/social', { params });
      return response;
    },
    
    /**
     * Get engagement metrics
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} Engagement metrics data
     */
    getEngagementMetrics: async (params = {}) => {
      const response = await api.get('/analytics/engagement', { params });
      return response;
    },
    
    /**
     * Get audience analytics
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} Audience analytics data
     */
    getAudienceAnalytics: async (params = {}) => {
      const response = await api.get('/analytics/audience', { params });
      return response;
    },
    
    /**
     * Get financial analytics
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} Financial analytics data
     */
    getFinancialAnalytics: async (params = {}) => {
      const response = await api.get('/analytics/financial', { params });
      return response;
    },
    
    /**
     * Get competitive analysis
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} Competitive analysis data
     */
    getCompetitiveAnalysis: async (params = {}) => {
      const response = await api.get('/analytics/competitive', { params });
      return response;
    },
    
    /**
     * Get regional analytics
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} Regional analytics data
     */
    getRegionalAnalytics: async (params = {}) => {
      const response = await api.get('/analytics/regional', { params });
      return response;
    },
    
    /**
     * Generate custom report
     * @param {Object} reportConfig - Report configuration
     * @returns {Promise<Object>} Custom report data
     */
    getCustomReport: async (reportConfig) => {
      const response = await api.post('/analytics/custom-report', reportConfig);
      return response;
    },
    
    /**
     * Export analytics data
     * @param {Object} params - Export parameters
     * @returns {Promise<Object>} Export data
     */
    exportAnalyticsData: async (params = {}) => {
      const response = await api.get('/analytics/export', { 
        params,
        responseType: 'blob'
      });
      return response;
    }
  }
});

// Create backwards-compatible exports that use the factory methods underneath
export const getDashboardAnalytics = async (params = {}) => {
  return analyticsServiceBase.getDashboardAnalytics(params);
};

export const getContentAnalytics = async (params = {}) => {
  return analyticsServiceBase.getContentAnalytics(params);
};

export const getCampaignAnalytics = async (campaignId, params = {}) => {
  return analyticsServiceBase.getCampaignAnalytics(campaignId, params);
};

export const getClientAnalytics = async (clientId, params = {}) => {
  return analyticsServiceBase.getClientAnalytics(clientId, params);
};

export const getBrandAnalytics = async (brandId, params = {}) => {
  return analyticsServiceBase.getBrandAnalytics(brandId, params);
};

export const getSocialMediaPerformance = async (params = {}) => {
  return analyticsServiceBase.getSocialMediaPerformance(params);
};

export const getEngagementMetrics = async (params = {}) => {
  return analyticsServiceBase.getEngagementMetrics(params);
};

export const getAudienceAnalytics = async (params = {}) => {
  return analyticsServiceBase.getAudienceAnalytics(params);
};

export const getFinancialAnalytics = async (params = {}) => {
  return analyticsServiceBase.getFinancialAnalytics(params);
};

export const getCompetitiveAnalysis = async (params = {}) => {
  return analyticsServiceBase.getCompetitiveAnalysis(params);
};

export const getRegionalAnalytics = async (params = {}) => {
  return analyticsServiceBase.getRegionalAnalytics(params);
};

export const getUserActivityAnalytics = async (params = {}) => {
  return analyticsServiceBase.getUserActivityAnalytics(params);
};

export const getCustomReport = async (reportConfig) => {
  return analyticsServiceBase.getCustomReport(reportConfig);
};

export const exportAnalyticsData = async (params = {}) => {
  return analyticsServiceBase.exportAnalyticsData(params);
};

/**
 * Get time tracking analytics
 * @param {Object} params - Query parameters
 * @returns {Promise<Object>} Time tracking analytics data
 */
export const getTimeTrackingAnalytics = async (params = {}) => {
  try {
    // Filter out null and undefined values
    const cleanParams = Object.entries(params).reduce((acc, [key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {});
    
    // Build query string manually
    const queryString = new URLSearchParams(cleanParams).toString();
    const url = queryString ? `/time-tracking-analytics?${queryString}` : '/time-tracking-analytics';
    
    const response = await api.get(url);
    return response;
  } catch (error) {
    console.error('Error fetching time tracking analytics:', error);
    // Re-throw with a more descriptive error message
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

/**
 * Export time tracking analytics
 * @param {Object} params - Query parameters
 * @returns {Promise<Object>} Export response
 */
export const exportTimeTrackingAnalytics = async (params = {}) => {
  // Filter out null and undefined values
  const cleanParams = Object.entries(params).reduce((acc, [key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      acc[key] = value;
    }
    return acc;
  }, {});
  
  // Build query string manually
  const queryString = new URLSearchParams(cleanParams).toString();
  const endpoint = queryString ? `/time-tracking-analytics/export?${queryString}` : '/time-tracking-analytics/export';
  
  const response = await api.get(endpoint, {
    responseType: 'blob'
  });
  
  // Create a download link
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `time-tracking-${new Date().toISOString().split('T')[0]}.xlsx`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  
  return response;
};